import React from 'react';
import { t } from '@lingui/core/macro';

import { Calendar, Rebook } from '@rover/icons';
import { Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';

type FrequencyToggleProps = {
  frequency?: ServiceFrequencyEnum;
  onChange: (serviceFrequency: ServiceFrequencyEnum) => void;
};

type OptionConfig = {
  label: string;
  value: ServiceFrequencyEnum;
  icon: React.ComponentType<{ width?: string; height?: string }>;
};

const FrequencyToggle = ({ frequency, onChange }: FrequencyToggleProps): JSX.Element => {
  const theme = useTheme();

  const options: OptionConfig[] = [
    { label: t`One Time`, value: ServiceFrequencyEnum.ONE_TIME, icon: Calendar },
    { label: t`Repeat Weekly`, value: ServiceFrequencyEnum.REPEAT, icon: Rebook },
  ];

  return (
    <Flex gap="3x" role="radiogroup" aria-label={t`Frequency`} mb="4x">
      {options.map(({ label, value, icon: Icon }) => {
        const selected = frequency === value;

        return (
          <Flex
            key={value}
            role="radio"
            aria-checked={selected}
            onPress={() => onChange(value)}
            flex="1"
            alignItems="center"
            gap="2x"
            padding="4x"
            borderRadius="primary"
            border="2px solid"
            borderColor={
              selected
                ? theme.colors.interactive_link.primary_base
                : theme.colors.interactive_border.primary_base
            }
            background={selected ? undefined : 'primary'}
            sx={
              selected
                ? { background: theme.colors.interactive_link.primary_inverse_pressed }
                : undefined
            }
          >
            <Icon width="24px" height="24px" />
            <Text size="100" fontWeight="semibold" textColor={selected ? 'secondary' : 'tertiary'}>
              {label}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default FrequencyToggle;
